import { Utils } from '@common';
import { React, Styled } from '@common/3rd';
import { DeskComponentRegistry, ModelWidget } from '@component';
import {
	CodeItem,
	CodesWidget,
	DeskCodesLabelProps,
	DeskLabelComponents,
	DeskLabellingLabelProps,
	DeskLabelProps,
	DeskLabelState,
	StyledPropsTheme,
	LabelDecorator,
} from '@my-types';

class DeskLabel<P extends DeskLabelProps & {decorator?: LabelDecorator;}, S extends DeskLabelState, C extends DeskLabelComponents> extends ModelWidget<
	P,
	S,
	C
> {
	labelRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.labelRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.span.attrs({
				'data-widget': 'desk-label',
				tabIndex: 0
			})`
				height: ${props => props.theme.INPUT_HEIGHT}px;
				line-height: ${props => props.theme.INPUT_HEIGHT}px;
				outline: none;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	renderLabel() {
		if (this.getPropName() != null && !Utils.isEmpty(this.getPropName())) {
			return this.decorate(this.getValueFromModel());
		} else {
			return this.decorate(this.getLabel());
		}
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box onFocus={this.onLabelFocus} onBlur={this.onLabelBlur} ref={this.labelRef}>
				{this.props.children}
				{this.renderLabel()}
			</C.Box>
		);
	}
	private onLabelFocus = () => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	private onLabelBlur = () => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
	focus(): this {
		this.labelRef.current.focus();
		return this;
	}
	getLabel() {
		return this.props.label;
	}
	protected getDecorator(): LabelDecorator | undefined {
		return this.props.decorator;
	}
	protected decorate(decorated: any): any {
		const decorator = this.getDecorator();
		if (decorator) {
			return decorator.call(this, {
				model: this.getModel(),
				root: this.getRootModel(),
				arrayHolder: this.getArrayHolder(),
				invoker: this,
				value: this.getValueFromModel(),
				decorated
			});
		} else {
			return decorated;
		}
	}
}

class DeskLabellingLabel<
	P extends DeskLabellingLabelProps,
	S extends DeskLabelState,
	C extends DeskLabelComponents
> extends DeskLabel<P, S, C> {
	protected initComponents(): C {
		const C = super.initComponents();
		return {
			Box: Styled(C.Box).attrs({
				'data-widget': 'desk-labelling-label',
				'data-required': this.props.required
			})`
				font-weight: ${props => props.theme.FONT_WEIGHT_BOLD_LIGHT};
				${() => this.paintCursor()}
				${props => this.paintColor(props.theme)}
				
				&[data-required=true]:after {
					// content: '\f069';
					content: '*';
					text-align: center;
					-moz-osx-font-smoothing: grayscale;
					-webkit-font-smoothing: antialiased;
					display: inline-block;
					font-style: normal;
					-webkit-font-feature-settings: normal;
					font-feature-settings: normal;
					font-variant: normal;
					text-rendering: auto;
					line-height: 1;
					// width: 1.25em;
					// font-family: 'Font Awesome 5 Free';
					// font-weight: 900;
					// transform: scale(0.6);
					color: ${props => props.theme.COLOR_DANGER};
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box onClick={this.onClicked}>{this.getLabel()}</C.Box>;
	}
	focus(): this {
		this.setState({
			focus: true
		});
		return this;
	}
	blur(): void {
		this.setState({
			focus: false
		});
	}
	paintColor(theme: StyledPropsTheme): string {
		return this.state.focus ? `color: ${theme.COLOR_PRIMARY};` : '';
	}
	paintCursor(): string {
		return this.props.labellingParent ? 'cursor: pointer;' : '';
	}
	onClicked = (): void => {
		this.props.labellingParent!.focusLabelled();
	};
}

class DeskCodesLabel<P extends DeskCodesLabelProps, S extends DeskLabelState, C extends DeskLabelComponents>
	extends DeskLabel<P, S, C>
	implements CodesWidget {
	renderLabel() {
		if (this.getPropName() != null && !Utils.isEmpty(this.getPropName())) {
			const value = this.getValueFromModel();
			// eslint-disable-next-line
			const codeItem = this.getCodes().find(code => code.v == value);
			return codeItem && codeItem.l;
		}
	}
	isClearable(): boolean {
		return Utils.isUndefined(this.props.clearable) ? true : (this.props.clearable as boolean);
	}
	getCodes(): CodeItem[] {
		return Utils.toArray(this.props.codes);
	}
	/**
	 * 让label属性失效
	 */
	getLabel() {
		return '';
	}
}

DeskComponentRegistry.register({
	label: DeskLabel,
	codesLabel: DeskCodesLabel
}).asReadOnly(DeskLabel, DeskCodesLabel);

export { DeskLabel, DeskLabellingLabel, DeskCodesLabel };
